import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Mail, MessageCircle, Phone, TriangleAlert } from "lucide-react";
import { PROTOCOLS } from "../lib/protocols";
import { PHONE_OFFICE, PHONE_OFFICE_DISPLAY, EMAIL_DIREZIONE } from "../lib/api";
import { openDiagnostica } from "../components/DiagnosticaModal";
import { scrollToTop, useSectionNavigation } from "../lib/navigation";
import Seo from "../components/Seo";

export default function ProtocolPage() {
  const { slug } = useParams();
  const protocol = PROTOCOLS.find((p) => p.slug === slug);
  const goToSection = useSectionNavigation();

  useEffect(() => {
    scrollToTop();
  }, [slug]);

  if (!protocol) return <Navigate to="/" replace />;

  // Ogni CTA di pagina protocollo apre il modulo di prenotazione contestuale (configurazione dalla pagina corrente)
  const onPrimaryCta = () => openDiagnostica();

  const others = PROTOCOLS.filter((p) => p.slug !== slug);
  const waHref = `https://wa.me/393514129152?text=${encodeURIComponent(
    `Salve, ho visto il sito di A.C. HUB e vorrei informazioni sul protocollo ${protocol.name}.`
  )}`;

  return (
    <main className="pb-0" data-testid="protocol-page">
      <Seo
        title={`${protocol.name} — ${protocol.tagline} | A.C. HUB`}
        description={protocol.description}
        path={`/protocollo/${protocol.slug}`}
        image={`/og-${protocol.slug}.jpg`}
      />
      <section className="relative overflow-hidden pt-[150px]" data-testid="protocol-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={protocol.heroImage}
            alt={protocol.name}
            className="h-full w-full object-cover"
            style={{ filter: "grayscale(0.6) brightness(0.3)" }}
          />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(0deg, #050505 8%, rgba(5,5,5,0.75) 55%, rgba(5,5,5,0.5))" }}
          />
        </div>

        <div className="container-hub relative z-[2] pb-20 pt-8">
          <Link
            to="/"
            data-testid="protocol-back-link"
            onClick={(e) => {
              e.preventDefault();
              goToSection("protocolli");
            }}
            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#929292] transition-colors hover:text-gold-light"
          >
            <ArrowLeft size={13} /> Torna ai 5 protocolli
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="mt-9 inline-flex items-baseline gap-3 border border-gold bg-[#0b0b0b]/80 px-5 py-3.5" data-testid="protocol-identifier">
              <span className="font-serif text-[30px] leading-none text-gold-light">{protocol.number}</span>
              <span className="text-[11px] font-bold leading-none tracking-[0.14em] text-gold-light">{protocol.name}</span>
            </div>
            <p className="mt-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold-light" data-testid="protocol-subtitle">
              {protocol.subtitle}
            </p>
            <h1 className="display mt-6 max-w-[820px] text-4xl text-cream sm:text-5xl lg:text-[58px]" data-testid="protocol-title">
              {protocol.title || protocol.name}
            </h1>
            <p className="lead mt-7 max-w-[680px]" data-testid="protocol-description">
              {protocol.intro || protocol.description}
            </p>
            {protocol.highlights && (
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-testid="protocol-highlights">
                {protocol.highlights.map((h) => (
                  <span
                    key={h}
                    className="flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#c9c6bf]"
                  >
                    <span className="h-1 w-1 shrink-0 bg-gold" /> {h}
                  </span>
                ))}
              </div>
            )}
            {!protocol.ctaLabels && (
              <div className="mt-10 flex flex-wrap gap-4">
                <button data-testid="protocol-lead-cta" onClick={onPrimaryCta} className="btn btn-gold">
                  {protocol.ctaLabels?.primary || "Attiva questo protocollo"}
                  <ArrowUpRight size={14} />
                </button>
                <a href={PHONE_OFFICE} data-testid="protocol-call-cta" className="btn btn-outline">
                  <Phone size={13} /> {protocol.ctaLabels?.secondary || PHONE_OFFICE_DISPLAY}
                </a>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {protocol.scenarios && (
        <section className="section !pt-0" data-testid="protocol-scenarios">
          <div className="container-hub">
            <h2
              className="section-title max-w-[760px] text-3xl sm:text-4xl lg:text-5xl"
              data-testid="protocol-scenarios-title"
            >
              {protocol.scenarios.title}
            </h2>
            <p className="lead mt-6 max-w-[620px]" data-testid="protocol-scenarios-subtitle">
              {protocol.scenarios.subtitle}
            </p>
            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              {protocol.scenarios.items.map((s, i) => (
                <motion.article
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
                  className="border border-[#c9a45c]/22 bg-[#141414] p-7 transition-colors duration-300 hover:border-[#c9a45c]/45"
                  data-testid={`protocol-scenario-${i + 1}`}
                >
                  <span className="font-mono text-[10px] tracking-[0.16em] text-gold">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <h3 className="mt-4 font-serif text-[21px] font-medium leading-snug text-gold-light">{s.title}</h3>
                  <p className="mt-3 text-[14px] leading-[1.7] text-[#c9c6bf]">{s.text}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={protocol.scenarios ? "section !pt-0" : "section !pt-20"} data-testid="protocol-services">
        <div className="container-hub">
          <p className="eyebrow">{protocol.scenarios ? "Le cinque fasi" : "Cosa include"}</p>
          <h2 className="section-title max-w-[760px] text-3xl sm:text-4xl lg:text-5xl" data-testid="protocol-services-title">
            Tutto il percorso, fase per fase.
          </h2>

          <div className="mt-14 grid gap-4">
            {protocol.services.map((s, i) => (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.65, delay: 0.03 * i, ease: [0.16, 1, 0.3, 1] }}
                className={`grid overflow-hidden border border-[#c9a45c]/22 bg-[#141414] md:min-h-[210px] ${
                  i % 2 === 0 ? "md:grid-cols-[280px_1fr]" : "md:grid-cols-[1fr_280px]"
                }`}
                data-testid={`protocol-service-${i + 1}`}
              >
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className={`h-[150px] w-full object-cover sm:h-[190px] md:h-full ${i % 2 !== 0 ? "md:order-2" : ""}`}
                  style={{ filter: "grayscale(0.6) brightness(0.72)" }}
                />
                <div className="p-6 sm:p-9">
                  <span className="font-mono text-[10px] tracking-[0.16em] text-gold">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <h3 className="mt-4 font-serif text-[22px] font-medium leading-snug text-gold-light sm:text-[23px]">{s.title}</h3>
                  <p className="mt-4 max-w-[640px] text-[15px] leading-[1.75] text-[#dedbd4]">{s.text}</p>
                  {s.outcome && (
                    <div
                      className="mt-5 border-l-2 border-gold bg-[#0d0d0d] px-4 py-3.5"
                      data-testid={`protocol-outcome-${i + 1}`}
                    >
                      <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-gold">Cosa ottieni</p>
                      <p className="mt-1.5 text-[13px] leading-relaxed text-[#dedbd4]">{s.outcome}</p>
                    </div>
                  )}
                </div>
              </motion.article>
            ))}
          </div>

          {protocol.notice && (
            <div className="mt-8 flex items-start gap-4 border border-[#c9a45c]/40 bg-[#101010] p-6" data-testid="protocol-notice">
              <TriangleAlert size={18} className="mt-0.5 shrink-0 text-gold" />
              <p className="text-[13px] leading-relaxed text-[#b9b6af]">{protocol.notice}</p>
            </div>
          )}
        </div>
      </section>

      {protocol.finalSection && (
        <section className="section !pt-0" data-testid="protocol-final">
          <div className="container-hub">
            <div className="border border-[#c9a45c]/30 bg-[#0d0d0d] p-9 sm:p-12" data-testid="protocol-final-block">
              <p className="eyebrow">Il valore strategico</p>
              <h2
                className="display mt-5 max-w-[720px] text-3xl text-cream sm:text-4xl"
                data-testid="protocol-final-title"
              >
                {protocol.finalSection.title}
              </h2>
              <p className="lead mt-6 max-w-[680px]" data-testid="protocol-final-text">
                {protocol.finalSection.text}
              </p>
            </div>
          </div>
        </section>
      )}

      <section className="section !pt-0" data-testid="protocol-cta">
        <div className="container-hub">
          <div className="border border-[#c9a45c]/30 bg-[#0d0d0d] p-6 text-center sm:p-14" data-testid="protocol-cta-block">
            {protocol.finalCta ? (
              <>
                <p className="eyebrow eyebrow-center justify-center" data-testid="protocol-final-eyebrow">
                  {protocol.finalCta.eyebrow}
                </p>
                <h2
                  className="display mx-auto mt-5 max-w-[700px] text-3xl text-cream sm:text-4xl"
                  data-testid="protocol-cta-title"
                >
                  {protocol.finalCta.title}
                </h2>
                <p className="lead mx-auto mt-5 max-w-[560px]" data-testid="protocol-final-desc">
                  {protocol.finalCta.description}
                </p>
                <p
                  className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold-light"
                  data-testid="protocol-final-info"
                >
                  {protocol.finalCta.info}
                </p>
                <div className="mt-8">
                  <button
                    data-testid="protocol-final-lead"
                    onClick={onPrimaryCta}
                    className="btn btn-gold w-full !min-h-[52px] !px-8 sm:w-auto"
                  >
                    {protocol.finalCta.button}
                  </button>
                  <p className="mt-3 text-[11px] text-[#a8a5a0]" data-testid="protocol-final-microtext">
                    {protocol.finalCta.microtext}
                  </p>
                </div>
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="protocol-whatsapp-cta"
                    className="btn btn-outline w-full !min-h-[46px] sm:w-auto"
                  >
                    <MessageCircle size={13} /> WhatsApp
                  </a>
                  <a
                    href={`mailto:${EMAIL_DIREZIONE}`}
                    data-testid="protocol-email-cta"
                    className="btn btn-outline w-full !min-h-[46px] sm:w-auto"
                  >
                    <Mail size={13} /> {EMAIL_DIREZIONE}
                  </a>
                </div>
              </>
            ) : (
              <>
                <p className="eyebrow eyebrow-center justify-center">Parlane con noi</p>
                <h2 className="display mx-auto mt-5 max-w-[700px] text-3xl text-cream sm:text-4xl">
                  Pronto ad attivare <span className="gold-text">{protocol.name}?</span>
                </h2>
                <p className="lead mx-auto mt-5 max-w-[560px]">
                  Prima analisi riservata e senza impegno: valutiamo insieme se questo protocollo è quello giusto
                  per il tuo obiettivo.
                </p>
                <div className="mt-9 flex flex-wrap justify-center gap-4">
                  <button data-testid="protocol-final-lead" onClick={onPrimaryCta} className="btn btn-gold">
                    {protocol.ctaLabels?.finalPrimary || "Richiedi il tuo percorso"}
                  </button>
                  <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="protocol-whatsapp-cta" className="btn btn-outline">
                    <MessageCircle size={13} /> WhatsApp
                  </a>
                  <a href={`mailto:${EMAIL_DIREZIONE}`} data-testid="protocol-email-cta" className="btn btn-outline">
                    <Mail size={13} /> {EMAIL_DIREZIONE}
                  </a>
                </div>
              </>
            )}
          </div>

          <div className="mt-16" data-testid="protocol-others">
            <p className="mb-6 text-[9px] font-bold tracking-[0.25em] text-gold">GLI ALTRI PROTOCOLLI</p>
            <div className="grid grid-cols-1 gap-px border border-[#c9a45c]/22 bg-[#c9a45c]/22 sm:grid-cols-2 lg:grid-cols-4">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/protocollo/${p.slug}`}
                  data-testid={`protocol-other-${p.slug}`}
                  className="group flex min-h-[150px] flex-col justify-between bg-[#0b0b0b] p-6 transition-colors duration-300 hover:bg-[#131313]"
                >
                  <span className="font-mono text-[10px] tracking-[0.16em] text-gold">{p.number}</span>
                  <div>
                    <h3 className="display text-[21px] text-cream group-hover:text-gold-light">{p.name}</h3>
                    <p className="mt-1.5 text-[12px] leading-snug text-[#929292]">{p.tagline}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
